import React, { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Input, InputNumber, Radio, Select, Typography } from 'antd';

import EventFormElement from '../events/EventFormElement';
import { CardOptionLabel } from '../cards/CardVisuals';
import { CLOSE } from '../icons';
import { cardChain } from '../../utils/cards';
import { getElementInitialValue, isElementValueEmpty } from '../../utils/elements';
import { normalizeId } from '../../utils/ids';
import { collectionZone } from '../../utils/collectionTimezone';
import { useCollectionContext } from '../../contexts/CollectionContext';
import useElementGrants from '../../hooks/useElementGrants';
import { governingGrant, LEVELS, PUBLIC_LABEL } from '../../utils/elementAccess';
import { cardPagePath } from '../../utils/cards';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import {
	cardIntakeRefusal,
	entryLimitsForSave,
	entryLimitsProblem,
	groupLimitsOf,
	intakeBlockReason,
	isFileElement,
	labelOf,
	mustBeCovered,
	prefillBlockReason,
	privateBlockReason,
	uncoveredRequired,
	DEFAULT_UPLOADS,
	MAX_COPY,
	MAX_FIELDS,
	MAX_HEADLINE,
	MAX_UPLOAD_FILE_MB,
	MAX_UPLOAD_FILES,
	MAX_UPLOAD_SUBMISSION_MB,
} from '../../utils/intakeLinks';
import ExternalLink from '../common/ExternalLink';

const { Text } = Typography;

/**
 * THE FORM DEFINITION, ONE EDITOR FOR BOTH KINDS OF PUBLIC FORM (KPR-513;
 * keepr-api docs/plans/permanent-public-forms.md § Web surfaces, ruling f01:
 * "two kinds that share the form builder").
 *
 * An invite link (IntakeLinkModal) and a permanent public form
 * (PublicFormModal) ask a stranger the same thing in the same way — which
 * card, which of its fields, which privately, what is filled in for them,
 * what files they may add, the words they read — and the API judges both with
 * one module (`utils/formDefinition.js`, code for code). So the web has one
 * editor too: this hook holds every rule about the definition, and the
 * component renders it, top to bottom, from **Card** to **After they send it**
 * (public forms no longer offer a notes box, KPR-792).
 * What differs by kind is the frame around it — the warning above, and the
 * footer below (Stop after / Expires after for an invite link; the address
 * and the limits for a public form) — and each modal owns that.
 *
 * It was cut out of IntakeLinkModal as a MOVE: the invite link's markup, its
 * order and its words are byte-for-byte what they were, which the existing
 * IntakeLink tests prove by passing unedited. Rejected: a second copy for the
 * public form — two copies of these rules would drift the first time a data
 * type learned a new refusal, and the API would then refuse what one of the
 * two editors offered.
 *
 * The state (`state`, `setState`) belongs to the modal: the unsaved-changes
 * guard measures the whole of it, the kind's own footer fields included.
 * The definition's keys in it are `cardId`, `fields`, `privateFields`,
 * `prefill`, `uploads`, `headline`, `intro`, `confirmation`,
 * `shownFields` with `hadShown` (KPR-519, below), `entryLimits` with
 * `hadEntryLimits` (KPR-689, below), and `privateRequired` with
 * `hadPrivateRequired` (KPR-803, below).
 *
 * REQUIRED PRIVATE QUESTIONS (KPR-803; keepr-api § Intake links,
 * `privateRequired`). A card element that is required can never be a private
 * question (the record could never satisfy it). The FORM may require one
 * instead: a private question offers **Required** beside **Private**, the
 * card element stays optional, and the API serves it to the visitor as a
 * required question and refuses a submission that leaves it empty. The
 * requirement follows the question: one that stops being private, asked or
 * on the form loses it.
 *
 * REPEATING GROUPS (KPR-689; keepr-api § Intake links, "Repeating groups on
 * a form"). A group is asked like any field — the visitor adds its entries on
 * the form — and a group the form asks offers **Fewest** and **Most** entries
 * for THIS form beneath its row, each blank for the group's own number and
 * only ever narrower than it (the API refuses a wider one in the same words
 * the row says). A group is never private, filled in or shown: its row says
 * why where the choice would have been.
 *
 * SHOWN ELEMENTS (KPR-519; keepr-api § Intake links, refusal R10). An element
 * rule may say what a PUBLIC FORM VISITOR may do with an element: hidden, see
 * only, or edit (no rule: edit). The API holds every form to it — it asks
 * only what the public may edit, and SHOWS read-only, at its default, what
 * the public may only see (the Salem form's "Status: New"). So each row
 * offers what its element's public level allows, through the one checkbox it
 * always had: an element the public may edit is ASKED (as before); one they
 * may only see is SHOWN; one hidden from them is neither, disabled with its
 * reason. ⚑ Rejected: an Ask / Show / Leave off control on every row — for
 * any one element only two of the three are ever open, and a third segment
 * that is always disabled is a control that only says no. The levels come
 * from the card's element rules (hooks/useElementGrants: a manager, the
 * switch on); without them every row is as it always was, and the API's
 * refusal says what this form could not know.
 */

export const defaultUploads = () => ({ ...DEFAULT_UPLOADS, types: [...DEFAULT_UPLOADS.types] });

/** The definition half of a blank form. */
export const blankDefinition = () => ({
	cardId: null,
	fields: [],
	prefill: {},
	headline: '',
	intro: '',
	confirmation: '',
	uploads: defaultUploads(),
	// KPR-389: asked PRIVATELY — never also in `fields`.
	privateFields: [],
	// KPR-519: SHOWN read-only at its default — never asked. `hadShown`: the
	// form already showed something, so an emptied list goes up (and clears
	// it); a form that never did sends no such key.
	shownFields: [],
	hadShown: false,
	// KPR-689: `{ <group>: { min?, max? } }`, the fewest and most entries this
	// form takes; `hadEntryLimits`: the form already set some, so an emptied
	// map goes up (and clears them).
	entryLimits: {},
	hadEntryLimits: false,
	// KPR-803: the private questions the FORM requires — each one of
	// `privateFields`. `hadPrivateRequired`: the form already required some,
	// so an emptied list goes up (and clears it).
	privateRequired: [],
	hadPrivateRequired: false,
});

/**
 * The definition half of a stored form. An invite link carries these keys at
 * its top level; a public form under `definition` — the caller passes
 * whichever object holds them, plus the card id.
 */
export const definitionFrom = (source, cardId) => ({
	cardId: normalizeId(cardId) || null,
	fields: Array.isArray(source?.fields) ? source.fields : [],
	prefill: source?.prefill && typeof source.prefill === 'object' ? { ...source.prefill } : {},
	headline: source?.headline || '',
	intro: source?.intro || '',
	confirmation: source?.confirmation || '',
	uploads: source?.uploads && typeof source.uploads === 'object' ? { ...defaultUploads(), ...source.uploads } : defaultUploads(),
	privateFields: Array.isArray(source?.privateFields) ? source.privateFields : [],
	shownFields: Array.isArray(source?.shownFields) ? source.shownFields : [],
	hadShown: Array.isArray(source?.shownFields) && source.shownFields.length > 0,
	entryLimits: source?.entryLimits && typeof source.entryLimits === 'object' ? { ...source.entryLimits } : {},
	hadEntryLimits: Boolean(source?.entryLimits && typeof source.entryLimits === 'object' && Object.keys(source.entryLimits).length),
	privateRequired: Array.isArray(source?.privateRequired) ? source.privateRequired : [],
	hadPrivateRequired: Array.isArray(source?.privateRequired) && source.privateRequired.length > 0,
});

// The "takes" choice: photos alone, or photos and PDFs (the API also takes
// PDFs alone; this form offers the two a public form needs ⚑).
const TYPE_CHOICES = [
	{ value: 'photo', label: 'Photos', types: ['photo'] },
	{ value: 'photo+pdf', label: 'Photos and PDFs', types: ['photo', 'pdf'] },
];
const typeChoiceOf = types => (Array.isArray(types) && types.includes('pdf') ? 'photo+pdf' : 'photo');

/** Blocked as an input, open as a prefill: a record lookup. */
const prefillOnlyElement = el => Boolean(intakeBlockReason(el)) && !prefillBlockReason(el);

/**
 * The name an API refusal says is not on the card, or null: a field
 * (intake_unknown_field, `element`) or a prefill key (unknown_key at
 * `prefill.<name>`, or `definition.prefill.<name>` on a public form).
 */
export const staleNameOf = (err) => {
	const data = err?.response?.data || {};
	if (data.code === 'intake_unknown_field' && data.element) return `${data.element}`;
	if (data.code === 'unknown_key' && typeof data.path === 'string') {
		const path = data.path.startsWith('definition.') ? data.path.slice('definition.'.length) : data.path;
		if (path.startsWith('prefill.')) return path.slice('prefill.'.length) || null;
	}
	return null;
};

/**
 * Every rule about the definition, read from the modal's state. Answers what
 * the editor renders and what the modal saves: the fields, prefill and
 * private questions as they will go up (stale names left out), whether a
 * file field is on the form and its upload settings, and `problem` — the
 * first thing that would stop the save, said before it is pressed (null when
 * none, or before a card is chosen).
 */
export function useFormDefinition({
	state,
	setState,
	collectionId,
	cardOptions = [],
	cardsById = {},
	getElementsForCard = null,
}) {
	// A prefill starts at the element's default, read in the collection's zone
	// (the owner's ruling B11) — the day the form's own create would store.
	const contextCollection = useCollectionContext()?.collection || null;
	const ownCollection = normalizeId(contextCollection?._id) === normalizeId(collectionId) ? contextCollection : null;
	const zone = collectionZone(ownCollection);
	// A form takes files only where the collection's attachments are on
	// (KPR-388). Unknown here (no collection in context): offered, and the
	// API's refusal says why if they are off.
	const attachmentsOn = ownCollection ? ownCollection.allowAttachments === true : true;
	// A published global collection (KPR-389): what a stranger sends becomes
	// readable by every account once accepted, so the dialog says so.
	const published = ['published', 'retiring'].includes(ownCollection?.publication?.state) || ownCollection?.kind === 'global';
	const blockOf = useCallback(el => intakeBlockReason(el, { attachmentsOn }), [attachmentsOn]);
	// Names the API refused as not on the card although this client still
	// sees them (an element set or a parent not yet refreshed): they join the
	// "No longer on the card" rows, so the refusal points at something.
	const [refused, setRefused] = useState([]);

	const set = useCallback(patch => setState(prev => ({ ...prev, ...patch })), [setState]);

	const elementsFor = useCallback(
		id => (id && typeof getElementsForCard === 'function' ? (getElementsForCard(id) || []).filter(el => el?.name) : []),
		[getElementsForCard]
	);

	// Every member card, each with the reason it cannot take a form when it
	// has one. Disabled rather than hidden: "why is my Invoice card not
	// here?" has an answer, and the picker is where it is asked.
	const cardChoices = useMemo(() => cardOptions.map((option) => {
		const refusal = cardIntakeRefusal(cardChain(cardsById, option.value), elementsFor(option.value), { attachmentsOn });
		return { ...option, refusal, disabled: Boolean(refusal), searchText: option.card?.name || '' };
	}), [cardOptions, cardsById, elementsFor, attachmentsOn]);

	const { cardId } = state;
	const cardElements = useMemo(() => elementsFor(cardId), [elementsFor, cardId]);
	const elements = useMemo(
		() => (refused.length ? cardElements.filter(el => !refused.includes(el.name)) : cardElements),
		[cardElements, refused]
	);
	const known = useMemo(() => new Set(elements.map(el => el.name)), [elements]);

	// THE PUBLIC'S LEVEL on each element (KPR-519): the governing rule's
	// `public` level, or edit when no rule speaks for the public — the
	// resolver's answer for a form visitor (keepr-api authz/elementLevels.js).
	// `null` while the rules are not known (not a manager, the switch off, an
	// API without them): then nothing is offered that the API might refuse
	// for a reason this form cannot see, and nothing can be shown.
	const elementRules = useElementGrants(cardId ? collectionId : null, cardId || null);
	const rulesKnown = elementRules.available === true;
	const chainIds = useMemo(
		() => (cardId ? cardChain(cardsById, cardId).map(card => normalizeId(card?._id)).filter(Boolean) : []),
		[cardsById, cardId]
	);
	const publicLevelOf = useCallback((el) => {
		if (!rulesKnown || !el) return null;
		const found = governingGrant(elementRules.grants, chainIds.length ? chainIds : [normalizeId(cardId)], el.id);
		const rule = (found?.grant?.rules || []).find(entry => entry?.audience?.type === 'public');
		return LEVELS.includes(rule?.level) ? rule.level : 'edit';
	}, [rulesKnown, elementRules.grants, chainIds, cardId]);
	const restrictedForPublic = useCallback((el) => {
		const level = publicLevelOf(el);
		return level === 'see' || level === 'hidden';
	}, [publicLevelOf]);
	// STALE: a name this form still carries that the card no longer has — an
	// element deleted after the form was made. The API refuses it on every
	// save (intake_unknown_field for a field, unknown_key for a prefill), and
	// the public form already answers form_out_of_date for a stale prefill,
	// so these are SHOWN, under their own heading, and left out of the save.
	// Only judged once the card's elements are in hand: an empty list is a
	// card still loading, not a card with nothing on it.
	const judged = elements.length > 0;
	const staleFields = useMemo(
		() => (judged ? state.fields.filter(name => !known.has(name)) : []),
		[judged, state.fields, known]
	);
	const stalePrefill = useMemo(
		() => (judged ? Object.keys(state.prefill).filter(name => !known.has(name)) : []),
		[judged, state.prefill, known]
	);
	const stalePrivate = useMemo(
		() => (judged ? (state.privateFields || []).filter(name => !known.has(name)) : []),
		[judged, state.privateFields, known]
	);
	const staleShown = useMemo(
		() => (judged ? (state.shownFields || []).filter(name => !known.has(name)) : []),
		[judged, state.shownFields, known]
	);
	const elementsByNameAll = useMemo(() => Object.fromEntries(elements.map(el => [el.name, el])), [elements]);
	// The questions asked PRIVATELY (KPR-389), less any no longer on the card
	// (listed under "No longer on the card" and left out of the save) — and
	// less any the public may no longer edit (KPR-519: listed under "No longer
	// on the form" and left out, as the API narrows a stored one).
	const privateFields = useMemo(
		() => (state.privateFields || []).filter(name => known.has(name) && !restrictedForPublic(elementsByNameAll[name])),
		[state.privateFields, known, restrictedForPublic, elementsByNameAll]
	);
	// The private questions the FORM requires (KPR-803): only ever ones it
	// asks privately — the API refuses any other name.
	const privateRequired = useMemo(
		() => (state.privateRequired || []).filter(name => privateFields.includes(name)),
		[state.privateRequired, privateFields]
	);
	// The elements SHOWN read-only (KPR-519): on the card, and — when the
	// rules are known — still see only for the public. One that is not is
	// listed under "No longer on the form" and left out of the save.
	const shownFields = useMemo(
		() => (state.shownFields || []).filter((name) => {
			if (!known.has(name)) return false;
			const level = publicLevelOf(elementsByNameAll[name]);
			return level === null || level === 'see';
		}),
		[state.shownFields, known, publicLevelOf, elementsByNameAll]
	);
	// What the rules took off the form since it was saved: asked elements the
	// public may no longer edit, shown ones no longer see only for them.
	const ruleDropped = useMemo(() => {
		if (!rulesKnown) return [];
		const out = [];
		const note = (name, kind) => {
			const el = elementsByNameAll[name];
			if (!el) return;
			const level = publicLevelOf(el);
			if (kind !== 'shown' && (level === 'see' || level === 'hidden')) out.push({ name, label: labelOf(el), note: level === 'see' ? 'Asked for, but its element rules let public form visitors see it, not change it — tick it to show it read-only' : 'Asked for, but its element rules hide it from public form visitors' });
			if (kind === 'shown' && level !== 'see') out.push({ name, label: labelOf(el), note: level === 'hidden' ? 'Shown read-only, but its element rules hide it from public form visitors' : 'Shown read-only, but its element rules let public form visitors change it — tick it to ask for it' });
		};
		(state.fields || []).forEach(name => note(name, 'fields'));
		(state.privateFields || []).forEach(name => note(name, 'private'));
		(state.shownFields || []).forEach(name => note(name, 'shown'));
		return out;
	}, [rulesKnown, state.fields, state.privateFields, state.shownFields, elementsByNameAll, publicLevelOf]);
	const prefill = useMemo(() => {
		if (!stalePrefill.length) return state.prefill;
		const live = { ...state.prefill };
		stalePrefill.forEach((name) => { delete live[name]; });
		return live;
	}, [state.prefill, stalePrefill]);
	// Every field that is, or can become, required and is not filled in is
	// ON the form, whatever was ticked: the API refuses a form that could
	// never be completed, so those boxes are locked on. Folded in here rather
	// than seeded once, so an edit of a form made before a field became
	// required shows it on, too.
	const fields = useMemo(() => {
		const wanted = new Set(state.fields);
		elements.forEach((el) => {
			// Not one still asked PRIVATELY: a private question that became
			// required is a problem the manager resolves (below), never one
			// quietly moved to the public side. Not one the public may only see
			// or not see at all (KPR-519): it cannot be asked, and its default
			// covers it (an element rule needs one — keepr-api refusal R4).
			if (mustBeCovered(el) && !blockOf(el) && !(isFileElement(el) && !attachmentsOn) && !Object.prototype.hasOwnProperty.call(prefill, el.name) && !privateFields.includes(el.name) && !restrictedForPublic(el)) wanted.add(el.name);
		});
		privateFields.forEach(name => wanted.delete(name));
		// Neither one SHOWN, nor one the public may not edit (KPR-519).
		shownFields.forEach(name => wanted.delete(name));
		// A name no longer on the card is not here: it is listed under "No
		// longer on the card" and left out of the save (staleFields).
		return elements.filter(el => wanted.has(el.name) && !restrictedForPublic(el)).map(el => el.name);
	}, [state.fields, elements, prefill, blockOf, attachmentsOn, privateFields, shownFields, restrictedForPublic]);
	const elementsByName = useMemo(() => Object.fromEntries(elements.map(el => [el.name, el])), [elements]);
	const cardRefusal = useMemo(
		() => (cardId ? cardIntakeRefusal(cardChain(cardsById, cardId), elements, { attachmentsOn }) : null),
		[cardId, cardsById, elements, attachmentsOn]
	);
	// The form collects a file (KPR-388): what it takes is the manager's to say.
	const fileFields = fields.filter(name => isFileElement(elementsByName[name]));
	const hasFileField = fileFields.length > 0;
	const photosOnlyField = fileFields.find(name => elementsByName[name]?.options?.accept === 'image') || null;
	const cardOption = cardChoices.find(option => option.value === cardId) || null;

	const chooseCard = useCallback((nextId) => {
		setState(prev => ({ ...prev, cardId: nextId, fields: [], prefill: {}, shownFields: [], privateRequired: [] }));
	}, [setState]);

	// Shown (KPR-519): an element the public may only see is on the form
	// read-only — never asked, never filled in.
	const toggleShown = useCallback((name, on) => {
		setState((prev) => {
			const shownWithout = (prev.shownFields || []).filter(n => n !== name);
			return {
				...prev,
				fields: prev.fields.filter(n => n !== name),
				privateFields: (prev.privateFields || []).filter(n => n !== name),
				privateRequired: (prev.privateRequired || []).filter(n => n !== name),
				shownFields: on ? [...shownWithout, name] : shownWithout,
			};
		});
	}, [setState]);

	const toggleField = useCallback((name, on) => {
		setState((prev) => {
			const without = prev.fields.filter(n => n !== name);
			// Not asked at all any more: neither publicly nor privately.
			const privateWithout = (prev.privateFields || []).filter(n => n !== name);
			// Asked is never also shown (KPR-519): one the rules now let the
			// public edit moves from shown to asked.
			const shown = (prev.shownFields || []).filter(n => n !== name);
			const requiredWithout = (prev.privateRequired || []).filter(n => n !== name);
			return { ...prev, fields: on ? [...without, name] : without, privateFields: on ? (prev.privateFields || []) : privateWithout, privateRequired: on ? (prev.privateRequired || []) : requiredWithout, shownFields: shown };
		});
	}, [setState]);

	// Private (KPR-389): the question moves between the two lists, which the
	// API keeps disjoint — asked once, either publicly or privately.
	const togglePrivate = useCallback((name, on) => {
		setState((prev) => {
			const fieldsWithout = prev.fields.filter(n => n !== name);
			const privateWithout = (prev.privateFields || []).filter(n => n !== name);
			// Asked publicly again, it is no longer the form's required private
			// question (KPR-803): a public question is required only by its card.
			return on
				? { ...prev, fields: fieldsWithout, privateFields: [...privateWithout, name] }
				: { ...prev, fields: [...fieldsWithout, name], privateFields: privateWithout, privateRequired: (prev.privateRequired || []).filter(n => n !== name) };
		});
	}, [setState]);

	// Required (KPR-803): the visitor must answer this private question; the
	// card element stays optional.
	const togglePrivateRequired = useCallback((name, on) => {
		setState((prev) => {
			const without = (prev.privateRequired || []).filter(n => n !== name);
			return { ...prev, privateRequired: on ? [...without, name] : without };
		});
	}, [setState]);

	// ---- prefill: a fixed value the visitor never sees ----

	const prefillCandidates = useMemo(() => elements.filter(el => (
		!Object.prototype.hasOwnProperty.call(prefill, el.name)
		&& !prefillBlockReason(el)
		// A field the form asks for is not also filled in — unless it is only
		// on the form because it is required, in which case filling it in is
		// the other way to cover it.
		&& (!fields.includes(el.name) || mustBeCovered(el))
		&& !privateFields.includes(el.name)
		&& !shownFields.includes(el.name)
	)), [elements, prefill, fields, privateFields, shownFields]);

	const addPrefill = useCallback((name) => {
		const def = elementsByName[name];
		if (!def) return;
		setState(prev => ({
			...prev,
			fields: prev.fields.filter(n => n !== name),
			privateFields: (prev.privateFields || []).filter(n => n !== name),
			privateRequired: (prev.privateRequired || []).filter(n => n !== name),
			shownFields: (prev.shownFields || []).filter(n => n !== name),
			prefill: { ...prev.prefill, [name]: getElementInitialValue(def, { isNewRecord: true, zone }) ?? '' },
		}));
	}, [elementsByName, zone, setState]);

	const removeStale = useCallback((name) => {
		setState((prev) => {
			const nextPrefill = { ...prev.prefill };
			delete nextPrefill[name];
			return { ...prev, fields: prev.fields.filter(n => n !== name), privateFields: (prev.privateFields || []).filter(n => n !== name), privateRequired: (prev.privateRequired || []).filter(n => n !== name), shownFields: (prev.shownFields || []).filter(n => n !== name), prefill: nextPrefill };
		});
	}, [setState]);

	const removePrefill = useCallback((name) => {
		// A required field that stops being filled in goes back on the form
		// by itself (the lock above); anything else simply leaves.
		setState((prev) => {
			const nextPrefill = { ...prev.prefill };
			delete nextPrefill[name];
			return { ...prev, prefill: nextPrefill };
		});
	}, [setState]);

	const setPrefillValue = useCallback((name, value) => {
		setState(prev => ({ ...prev, prefill: { ...prev.prefill, [name]: value } }));
	}, [setState]);

	// ---- a repeating group's entries on this form (KPR-689) ----

	// `key` is 'min' or 'max'; a blank (or anything but a whole number) is
	// the group's own.
	const setEntryLimit = useCallback((name, key, value) => {
		setState((prev) => {
			const all = { ...(prev.entryLimits || {}) };
			const one = { ...(all[name] || {}) };
			if (Number.isInteger(value)) one[key] = value; else delete one[key];
			if (Object.keys(one).length) all[name] = one; else delete all[name];
			return { ...prev, entryLimits: all };
		});
	}, [setState]);

	// ---- what would stop the save, said before it is pressed ----

	const emptyPrefill = Object.keys(prefill).filter(name => isElementValueEmpty(elementsByName[name] || {}, prefill[name]));
	// An element the public may only see, or not see at all, is covered by its
	// default (an element rule refuses one without — keepr-api refusal R4),
	// shown or not (KPR-519).
	const uncovered = cardId ? uncoveredRequired(elements.filter(el => !restrictedForPublic(el)), fields, prefill, { attachmentsOn }) : [];
	const uploads = state.uploads || defaultUploads();
	const uploadsProblem = !hasFileField ? null
		: uploads.maxSubmissionMb < uploads.maxFileMb ? 'A submission must take at least one whole file: raise "A submission up to" to the file size or more.'
			: null;
	// A question asked privately that may no longer be (the card made it
	// required, a condition reads it): said, never moved quietly — the manager
	// switches Private off to ask it publicly, or stops asking it.
	const privateProblemName = privateFields.find(name => elementsByName[name] && (blockOf(elementsByName[name]) || privateBlockReason(elementsByName[name], elements)));
	const privateProblem = privateProblemName
		? `${labelOf(elementsByName[privateProblemName])} is asked privately, but it cannot be any more: ${blockOf(elementsByName[privateProblemName]) || privateBlockReason(elementsByName[privateProblemName], elements)} Switch Private off to ask it publicly, or stop asking it.`
		: null;
	// The groups the form asks, and the fewest and most entries it sets for
	// each (KPR-689) — only for a group it asks; the first that is wider than
	// its group's own is said before Save, in the API's words.
	const groupFields = fields.filter(name => isRepeatingGroup(elementsByName[name]));
	const entryLimits = entryLimitsForSave(state.entryLimits, groupFields);
	const entryLimitsName = groupFields.find(name => entryLimitsProblem(elementsByName[name], entryLimits[name]));
	const entryProblem = entryLimitsName ? entryLimitsProblem(elementsByName[entryLimitsName], entryLimits[entryLimitsName]) : null;
	const problem = !cardId ? null
		: cardRefusal ? cardRefusal.message
			: fields.length === 0 ? 'Choose at least one field for the form to ask for publicly.'
				: fields.length + privateFields.length + shownFields.length > MAX_FIELDS ? `A form can ask for and show at most ${MAX_FIELDS} fields, public, private and shown together.`
					: privateProblem ? privateProblem
						: uncovered.length ? (prefillOnlyElement(uncovered[0])
							? `${labelOf(uncovered[0])} can become required, and a record lookup can only be filled in for the visitor. Fill it in below.`
							: `${labelOf(uncovered[0])} can become required, so include it in the form or fill it in for the visitor.`)
							: emptyPrefill.length ? `Give ${labelOf(elementsByName[emptyPrefill[0]])} a value, or stop filling it in.`
								: entryProblem || uploadsProblem;

	/**
	 * The definition as it goes up, in the API's spelling: the keys an invite
	 * link takes at its top level and a public form under `definition`.
	 * `uploads` only with a file field on the form (the API refuses it
	 * without one); `privateFields` always the whole list; `shownFields`
	 * (KPR-519) when it has any, or when the form had some (an empty list
	 * clears them) — a form that never showed anything sends no such key.
	 */
	const definition = {
		fields,
		prefill,
		headline: state.headline,
		intro: state.intro,
		confirmation: state.confirmation,
		uploads: hasFileField ? uploads : undefined,
		privateFields,
		...(shownFields.length || state.hadShown ? { shownFields } : {}),
		// KPR-689: when it sets any, or when the form had some (an empty map
		// clears them) — a form that never did sends no such key.
		...(Object.keys(entryLimits).length || state.hadEntryLimits ? { entryLimits } : {}),
		// KPR-803: when it requires any, or when the form did (an empty list
		// clears them) — a form that never did sends no such key.
		...(privateRequired.length || state.hadPrivateRequired ? { privateRequired } : {}),
	};

	return {
		state,
		set,
		collectionId,
		published,
		attachmentsOn,
		blockOf,
		cardChoices,
		cardOption,
		cardId,
		elements,
		elementsByName,
		fields,
		prefill,
		privateFields,
		privateRequired,
		shownFields,
		staleFields,
		stalePrefill,
		stalePrivate,
		staleShown,
		ruleDropped,
		rulesKnown,
		publicLevelOf,
		cardRefusal,
		hasFileField,
		photosOnlyField,
		uploads,
		prefillCandidates,
		problem,
		definition,
		chooseCard,
		toggleField,
		togglePrivate,
		togglePrivateRequired,
		toggleShown,
		addPrefill,
		removeStale,
		removePrefill,
		setPrefillValue,
		setEntryLimit,
		entryLimits,
		setRefused,
	};
}

/**
 * A group's entries on this form (KPR-689): Fewest and Most, each blank for
 * the group's own number, with the group's range said beside them and the
 * API's sentence when one goes past it.
 */
function EntryLimitsRow({ element, limits, onChange, idPrefix }) {
	const own = groupLimitsOf(element);
	const problem = entryLimitsProblem(element, limits);
	const base = `${idPrefix}-entries-${element.name}`;
	return (
		<div className="intake-link-modal__entries" data-testid={`intake-link-entries-${element.name}`}>
			<div className="intake-link-modal__row">
				<div>
					<label className="share-links__field-label" htmlFor={`${base}-min`}>Fewest entries</label>
					<InputNumber
						id={`${base}-min`}
						min={0}
						precision={0}
						value={Number.isInteger(limits.min) ? limits.min : null}
						placeholder={String(own.min)}
						status={problem && Number.isInteger(limits.min) ? 'error' : undefined}
						onChange={value => onChange('min', Number.isInteger(value) ? value : null)}
					/>
				</div>
				<div>
					<label className="share-links__field-label" htmlFor={`${base}-max`}>Most entries</label>
					<InputNumber
						id={`${base}-max`}
						min={1}
						precision={0}
						value={Number.isInteger(limits.max) ? limits.max : null}
						placeholder={String(own.max)}
						status={problem && Number.isInteger(limits.max) ? 'error' : undefined}
						onChange={value => onChange('max', Number.isInteger(value) ? value : null)}
					/>
				</div>
			</div>
			<span className="intake-link-modal__reason">
				{problem || `Visitors add entries on the form. The card allows ${own.min ? `${own.min} to ${own.max}` : `up to ${own.max}`}; leave a box blank to use that, or narrow it for this form.`}
			</span>
		</div>
	);
}

/**
 * The editor, from **Card** to **After they send it**. `editing`: the card
 * never changes on an edit — a form for another card is another form.
 * `noun` names the thing in its hints ("link" or "form"); `idPrefix` keeps
 * the control ids apart; `nameField` renders between the stale rows and the
 * headline, where the invite link has always asked for its name.
 */
export default function FormDefinitionEditor({
	definition: model,
	editing = false,
	noun = 'link',
	idPrefix = 'intake-link',
	nameField = null,
	onCardChosen = null,
}) {
	const {
		state, set, blockOf, cardChoices, cardOption, cardId, elements, elementsByName, fields, prefill, privateFields,
		staleFields, stalePrefill, stalePrivate, cardRefusal, hasFileField, photosOnlyField, uploads,
		prefillCandidates, chooseCard, toggleField, togglePrivate, addPrefill, removeStale, removePrefill, setPrefillValue,
		shownFields = [], staleShown = [], ruleDropped = [], rulesKnown = false, publicLevelOf = () => null, toggleShown = () => {},
		collectionId = null, entryLimits = {}, setEntryLimit = () => {},
		privateRequired = [], togglePrivateRequired = () => {},
	} = model;
	// Where a field's element rules are changed (KPR-519): the card's Elements
	// tab, each element's Access — in a new tab, so this form keeps its state.
	const accessPath = cardId ? cardPagePath(cardId, collectionId, 'tab=elements') : null;
	const accessLink = accessPath
		? <ExternalLink href={accessPath} className="intake-link-modal__access-link">Open its Access</ExternalLink> : null;

	return (
		<>
			<div className="share-links__field">
				<label className="share-links__field-label" htmlFor={`${idPrefix}-card`}>Card</label>
				{editing ? (
					cardOption?.card
						? <CardOptionLabel card={cardOption.card} />
						: <Text type="secondary">This card is no longer in the collection.</Text>
				) : (
					<Select
						id={`${idPrefix}-card`}
						className="intake-link-modal__card"
						value={cardId || undefined}
						placeholder="Which card the form fills in"
						onChange={(next) => { chooseCard(next); onCardChosen?.(next); }}
						options={cardChoices}
						showSearch
						optionFilterProp="searchText"
						// The chosen card as its chip and name. The options carry no
						// `label`, so without this the box showed the card's raw id
						// once one was picked — on the invite link too, since KPR-388
						// (found building KPR-513, where the editor became shared).
						labelRender={({ value }) => {
							const chosen = cardChoices.find(option => option.value === value);
							return chosen?.card ? <CardOptionLabel card={chosen.card} /> : value;
						}}
						optionRender={option => (
							<span className="intake-link-modal__card-option">
								<CardOptionLabel card={option.data.card} depth={option.data.depth} />
								{option.data.refusal ? (
									<span className="intake-link-modal__reason">{option.data.refusal.message}</span>
								) : null}
							</span>
						)}
						data-testid="intake-link-card"
					/>
				)}
				{cardRefusal ? <p className="share-links__hint intake-link-modal__reason">{cardRefusal.message}</p> : null}
			</div>

			{cardId && !cardRefusal ? (
				<>
					<div className="share-links__field">
						<span className="share-links__field-label">What the form asks for</span>
						<p className="share-links__hint">
							Only the fields you tick. A field added to this card later is <strong>not</strong> asked
							for by this {noun}. Tick <strong>Private</strong> to keep an answer off the record: only the
							people who manage this collection see it, beside the submission.
						</p>
						{rulesKnown ? (
							<p className="share-links__hint" data-testid="intake-link-shown-hint">
								An element that {PUBLIC_LABEL.toLowerCase()} may <strong>see only</strong> is shown on the form
								read-only, at its default, instead of asked for; one hidden from them is never on it.
								That is set in each element&rsquo;s Access on the card.
								{accessPath ? <>{' '}<ExternalLink href={accessPath}>Open the card&rsquo;s elements</ExternalLink></> : null}
							</p>
						) : null}
						<ul className="intake-link-modal__fields" data-testid="intake-link-fields">
							{elements.map((el) => {
								// KPR-519: what the public may do with this element decides
								// what its checkbox means — Show (see only), nothing (hidden),
								// or Ask (edit, or no rules known: as it always was).
								const level = publicLevelOf(el);
								if (level === 'see' || level === 'hidden') {
									// The API's own rule for what can be shown (formDefinition
									// neverShownReason): never a file, nor what can never be on
									// an anonymous form at all.
									const showBlocked = level === 'see'
										? (isFileElement(el) ? 'A file belongs to one record and has no default, so a form cannot show it.'
											// KPR-689 (keepr-api neverShownReason): a group has no default entries.
											: isRepeatingGroup(el) ? 'A repeating group has no entries until somebody adds them, so a form cannot show it.'
												: blockOf(el))
										: null;
									const isShown = shownFields.includes(el.name);
									const isPrefilledHere = Object.prototype.hasOwnProperty.call(prefill, el.name);
									const shownNote = level === 'hidden'
										? 'Hidden from public form visitors by its element rules, so it is never on the form.'
										: isPrefilledHere ? 'Filled in for the visitor (below).'
											: showBlocked || (isShown
												? 'Shown read-only at its default: visitors see it and cannot change it.'
												: 'See only for public form visitors: tick to show it read-only, at its default.');
									return (
										<li key={el.name} className="intake-link-modal__field-row" data-public-level={level}>
											<div className="intake-link-modal__field-main">
												<Checkbox
													checked={level === 'see' && isShown}
													disabled={level === 'hidden' || Boolean(showBlocked) || isPrefilledHere}
													onChange={event => toggleShown(el.name, event.target.checked)}
													aria-label={level === 'see' ? `Show ${labelOf(el)} read-only` : undefined}
													data-testid={`intake-link-show-${el.name}`}
												>
													{labelOf(el)}
												</Checkbox>
												{level === 'see' && isShown ? <span className="intake-link-modal__shown-badge">Shown</span> : null}
											</div>
											<span className="intake-link-modal__reason">{shownNote}{' '}{accessLink}</span>
										</li>
									);
								}
								const blocked = blockOf(el);
								const isPrefilled = Object.prototype.hasOwnProperty.call(prefill, el.name);
								const isPrivate = privateFields.includes(el.name);
								const locked = !blocked && !isPrefilled && !isPrivate && mustBeCovered(el);
								const asked = fields.includes(el.name) || isPrivate;
								// Why it cannot be private — said only where it would be offered
								// (the field is asked) and the required note does not already say it.
								const privateReason = privateBlockReason(el, elements);
								const requiredWords = el.options?.required ? 'Required on this card' : 'Can become required on this card';
								// A required record lookup has exactly one way to be
								// covered, and the reason has to say which.
								const prefillOnly = blocked && !isPrefilled && mustBeCovered(el) && !prefillBlockReason(el);
								const note = (isPrefilled ? 'Filled in for the visitor (below).' : null)
									|| (prefillOnly ? `${requiredWords}, and a record lookup can only be filled in for the visitor — choose the record below.` : null)
									|| blocked
									|| (locked ? `${requiredWords}.` : null);
								return (
									<li key={el.name} className="intake-link-modal__field-row">
										<div className="intake-link-modal__field-main">
											<Checkbox
												checked={asked}
												disabled={Boolean(blocked) || isPrefilled || locked}
												onChange={event => toggleField(el.name, event.target.checked)}
											>
												{labelOf(el)}
											</Checkbox>
											{asked && !blocked ? (
												<span className="intake-link-modal__private-choices">
													<Checkbox
														className="intake-link-modal__private"
														checked={isPrivate}
														// Switching OFF is always open: it is how a private
														// question that may no longer be private is resolved.
														disabled={!isPrivate && Boolean(privateReason)}
														onChange={event => togglePrivate(el.name, event.target.checked)}
														aria-label={`Ask ${labelOf(el)} privately`}
														data-testid={`intake-link-private-${el.name}`}
													>
														Private
													</Checkbox>
													{isPrivate && !privateReason ? (
														<Checkbox
															className="intake-link-modal__private"
															checked={privateRequired.includes(el.name)}
															onChange={event => togglePrivateRequired(el.name, event.target.checked)}
															aria-label={`Require an answer to ${labelOf(el)}`}
															data-testid={`intake-link-private-required-${el.name}`}
														>
															Required
														</Checkbox>
													) : null}
												</span>
											) : null}
										</div>
										{note ? <span className="intake-link-modal__reason">{note}</span> : null}
										{asked && !blocked && !locked && privateReason ? (
											<span className="intake-link-modal__reason">{isPrivate ? privateReason : `Cannot be private. ${privateReason}`}</span>
										) : null}
										{asked && !blocked && !isPrivate && isRepeatingGroup(el) ? (
											<EntryLimitsRow
												element={el}
												limits={entryLimits[el.name] || {}}
												onChange={(key, value) => setEntryLimit(el.name, key, value)}
												idPrefix={idPrefix}
											/>
										) : null}
										{isPrivate && !privateReason ? (
											<span className="intake-link-modal__reason">
												{/* KPR-459: with element rules on, a private answer is kept on the
												    record under a managers-only rule the save writes. */}
												{rulesKnown
													? 'Only managers see the answer. It is kept on the record, hidden from everyone else, and only the form can write it.'
													: 'Only managers see the answer; it is never on the record.'}
												{/* KPR-803: required by the form, never by the card. */}
												{privateRequired.includes(el.name) ? ' The visitor must answer it on this form; it stays optional on the card.' : ''}
											</span>
										) : null}
									</li>
								);
							})}
						</ul>
					</div>

					<div className="share-links__field">
						<span className="share-links__field-label">Filled in for the visitor</span>
						<p className="share-links__hint">
							A value every submission gets, which the visitor never sees — the site, the vendor,
							the record it belongs to.
						</p>
						{Object.keys(prefill).map((name) => {
							const def = elementsByName[name];
							if (!def) return null;
							return (
								<div key={name} className="intake-link-modal__prefill-row">
									<div className="intake-link-modal__prefill-input">
										<EventFormElement
											event={{ elements: prefill }}
											element={def}
											value={prefill[name]}
											onChange={setPrefillValue}
											showIconBadge={false}
											siblingDefs={elements}
										/>
									</div>
									<Button
										type="text"
										icon={<CLOSE />}
										aria-label={`Stop filling in ${labelOf(def)}`}
										onClick={() => removePrefill(name)}
									/>
								</div>
							);
						})}
						{prefillCandidates.length ? (
							<Select
								className="intake-link-modal__prefill-add"
								value={null}
								placeholder="Fill in a field…"
								onChange={addPrefill}
								options={prefillCandidates.map(el => ({ value: el.name, label: labelOf(el) }))}
								data-testid="intake-link-prefill-add"
							/>
						) : null}
					</div>

					{hasFileField ? (
						<div className="share-links__field intake-link-modal__uploads" data-testid="intake-link-uploads">
							<span className="share-links__field-label">Files visitors can add</span>
							<p className="share-links__hint">
								Photos are saved again without their location, camera or other details before they are
								stored, and their file names are not kept. PDFs are kept exactly as sent. Files wait with
								the submission, and only the people who review it can open them.
							</p>
							<Radio.Group
								className="intake-link-modal__upload-types"
								value={typeChoiceOf(uploads.types)}
								onChange={event => set({ uploads: { ...uploads, types: TYPE_CHOICES.find(choice => choice.value === event.target.value).types } })}
								optionType="button"
								options={TYPE_CHOICES.map(choice => ({ value: choice.value, label: choice.label }))}
								aria-label="What the form takes"
							/>
							{photosOnlyField && typeChoiceOf(uploads.types) === 'photo+pdf' ? (
								<p className="share-links__hint">{`${labelOf(elementsByName[photosOnlyField])} takes photos only on this card, so it still takes no PDF.`}</p>
							) : null}
							<div className="intake-link-modal__row intake-link-modal__upload-caps">
								<div>
									<label className="share-links__field-label" htmlFor={`${idPrefix}-file-mb`}>Each file up to</label>
									<InputNumber
										id={`${idPrefix}-file-mb`}
										min={1}
										max={MAX_UPLOAD_FILE_MB}
										precision={0}
										value={uploads.maxFileMb}
										onChange={value => set({ uploads: { ...uploads, maxFileMb: Number.isInteger(value) ? value : DEFAULT_UPLOADS.maxFileMb } })}
										addonAfter="MB"
									/>
								</div>
								<div>
									<label className="share-links__field-label" htmlFor={`${idPrefix}-submission-mb`}>A submission up to</label>
									<InputNumber
										id={`${idPrefix}-submission-mb`}
										min={1}
										max={MAX_UPLOAD_SUBMISSION_MB}
										precision={0}
										value={uploads.maxSubmissionMb}
										onChange={value => set({ uploads: { ...uploads, maxSubmissionMb: Number.isInteger(value) ? value : DEFAULT_UPLOADS.maxSubmissionMb } })}
										addonAfter="MB"
									/>
								</div>
								<div>
									<label className="share-links__field-label" htmlFor={`${idPrefix}-max-files`}>At most</label>
									<InputNumber
										id={`${idPrefix}-max-files`}
										min={1}
										max={MAX_UPLOAD_FILES}
										precision={0}
										value={uploads.maxFiles}
										onChange={value => set({ uploads: { ...uploads, maxFiles: Number.isInteger(value) ? value : DEFAULT_UPLOADS.maxFiles } })}
										addonAfter="files"
									/>
								</div>
							</div>
						</div>
					) : null}

					{ruleDropped.length ? (
						<div className="share-links__field intake-link-modal__stale" data-testid="intake-link-rule-dropped">
							<span className="share-links__field-label">No longer on the form</span>
							<p className="share-links__hint">
								Element rules changed since this {noun} was saved. These are left out when you save.
							</p>
							<ul className="intake-link-modal__fields">
								{ruleDropped.map(({ name, label, note }) => (
									<li key={`dropped:${name}`} className="intake-link-modal__field-row intake-link-modal__stale-row">
										<span className="intake-link-modal__stale-name">{label}</span>
										<span className="intake-link-modal__reason">{note}</span>
									</li>
								))}
							</ul>
						</div>
					) : null}

					{staleFields.length || stalePrefill.length || stalePrivate.length || staleShown.length ? (
						<div className="share-links__field intake-link-modal__stale" data-testid="intake-link-stale">
							<span className="share-links__field-label">No longer on the card</span>
							<p className="share-links__hint">
								This {noun} still names these, but the card no longer has them. They are left out
								when you save.
							</p>
							<ul className="intake-link-modal__fields">
								{[
									...staleFields.map(name => ({ name, note: 'Asked for on the form' })),
									...stalePrivate.map(name => ({ name, note: 'Asked privately' })),
									...staleShown.map(name => ({ name, note: 'Shown read-only' })),
									...stalePrefill.map(name => ({ name, note: 'Filled in for the visitor' })),
								].map(({ name, note }) => (
									<li key={`${note}:${name}`} className="intake-link-modal__field-row intake-link-modal__stale-row">
										<span className="intake-link-modal__stale-name">{name}</span>
										<span className="intake-link-modal__reason">{note}</span>
										<Button
											size="small"
											className="intake-link-modal__stale-remove"
											aria-label={`Remove ${name}`}
											onClick={() => removeStale(name)}
										>
											Remove
										</Button>
									</li>
								))}
							</ul>
						</div>
					) : null}
				</>
			) : null}

			{nameField}

			<div className="share-links__field">
				<label className="share-links__field-label" htmlFor={`${idPrefix}-headline`}>Headline (optional)</label>
				<Input
					id={`${idPrefix}-headline`}
					maxLength={MAX_HEADLINE}
					value={state.headline}
					placeholder="What the visitor reads at the top — the card’s name if blank"
					onChange={event => set({ headline: event.target.value })}
				/>
			</div>

			<div className="share-links__field">
				<label className="share-links__field-label" htmlFor={`${idPrefix}-intro`}>Instructions (optional)</label>
				<Input.TextArea
					id={`${idPrefix}-intro`}
					autoSize={{ minRows: 2, maxRows: 6 }}
					maxLength={MAX_COPY}
					value={state.intro}
					onChange={event => set({ intro: event.target.value })}
				/>
			</div>

			<div className="share-links__field">
				<label className="share-links__field-label" htmlFor={`${idPrefix}-confirmation`}>After they send it (optional)</label>
				<Input.TextArea
					id={`${idPrefix}-confirmation`}
					autoSize={{ minRows: 1, maxRows: 4 }}
					maxLength={MAX_COPY}
					value={state.confirmation}
					placeholder="Thank you — your answers were sent."
					onChange={event => set({ confirmation: event.target.value })}
				/>
			</div>
		</>
	);
}
